import * as Sentry from '@sentry/react';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { getConfig, type Locale, LOCALES } from '@/constants/config';
import english from '@/localization/assets/english';
import finnish from '@/localization/assets/finnish';
import swedish from '@/localization/assets/swedish';
import { formatCurrency } from '@/localization/currency';
import { environment } from '@/utils/env.utils';

const resources: Record<Locale, typeof swedish | typeof english | typeof finnish> = {
  'sv-SE': swedish,
  'en-GB': english,
  'fi-FI': finnish,
} as const;

const initializeI18n = async () => {
  const locale = getConfig().locale;

  await i18n
    .use(initReactI18next) // passes i18n down to react-i18next
    .init({
      lng: locale,
      fallbackLng: false,
      supportedLngs: LOCALES,
      resources,
      interpolation: {
        escapeValue: false, // react already safes from xss => https://www.i18next.com/translation-function/interpolation#unescape
      },
      saveMissing: true, // Required for missingKey event to fire
    });

  i18n.on('missingKey', (languages, namespace, key, resolvedValue) => {
    if (environment.env === 'production') {
      Sentry.captureMessage(`Missing translation key: ${key}`, {
        level: 'warning',
        tags: { type: 'missing_translation' },
        extra: {
          languages,
          namespace,
          key,
          resolvedValue,
        },
      });
    } else {
      // eslint-disable-next-line no-console
      console.warn('Missing translation key detected:', { languages, namespace, key, resolvedValue });
    }
  });
  i18n.services.formatter?.add('currency', (value, _lng, options) => formatCurrency(value, options));
};

export default initializeI18n;
